import { rawString } from '../../Language/Language';
import { ItemsPreview } from '../../ItemsPreview/ItemsPreview';
import { hasAllResolutionsShipmentScope } from '../../../utils/reasonUtils';
import {
  getItemsInSameShipment,
  getShipmentIndexForItem,
} from '../../../utils/shipmentScopeUtils';
import { useWorkflow } from '../../../workflow/hooks';
import type { ShipmentItemsPreviewProps } from './ShipmentItemsPreview.types';

export const ShipmentItemsPreview = ({
  selectedReason,
  order,
  currentItemId,
  shipmentIndex,
  enableShipmentIndexLookup = true,
}: ShipmentItemsPreviewProps) => {
  const { data } = useWorkflow();

  // Check if the selected reason has all shipment scope resolutions
  if (!selectedReason || !hasAllResolutionsShipmentScope(selectedReason)) {
    return null;
  }

  // Guard against null order
  if (!order) {
    return null;
  }

  // Get the tracking number from the workflow data
  const trackingNumber =
    data?.items?.[currentItemId]?.itemSelection?.trackingNumber;

  // Get all items in the same shipment as the current item
  // Use the shipmentIndex from props if available, otherwise calculate it (if lookup is enabled)
  const effectiveShipmentIndex =
    shipmentIndex !== undefined
      ? shipmentIndex
      : enableShipmentIndexLookup
        ? getShipmentIndexForItem(order, currentItemId, trackingNumber)
        : undefined;
  const shipmentItems = getItemsInSameShipment(
    order,
    currentItemId,
    effectiveShipmentIndex,
    trackingNumber,
  );

  // Get the source item ID for virtual items
  const sourceItemId =
    data?.items?.[currentItemId]?.itemSelection?.sourceItemId;
  const itemIdToFilter = sourceItemId || currentItemId;

  // For preview: filter out the current item (or its source item for virtual items)
  const otherShipmentItems = shipmentItems.filter(
    (item) => item.id !== itemIdToFilter,
  );

  // For popup dialog: include all shipment items (including the original item)
  const allShipmentItems = shipmentItems;

  const itemCount = otherShipmentItems.length;

  if (itemCount === 0) {
    return null;
  }

  const itemCountText =
    rawString({
      langKey: 'pdx.reason_selection.shipment_preview.included_in_package',
      replacementTokens: { item_count: itemCount.toString() },
    }) ??
    `${itemCount} other item${itemCount === 1 ? '' : 's'} in this package`;

  const viewDetailsText =
    rawString({
      langKey: 'pdx.reason_selection.shipment_preview.view_items',
    }) ?? 'View items';

  const dialogTitle =
    rawString({
      langKey: 'pdx.reason_selection.shipment_preview.dialog_title',
      replacementTokens: { item_count: allShipmentItems.length.toString() },
    }) ??
    `${allShipmentItems.length} item${allShipmentItems.length === 1 ? '' : 's'}`;

  return (
    <ItemsPreview
      items={otherShipmentItems}
      itemCountText={itemCountText}
      viewDetailsText={viewDetailsText}
      dialogTitle={dialogTitle}
      variant="bordered"
    />
  );
};
